import TimeService from "./timeService/timeService";

// If you make any changes to this list, please add @igor.kalkov to the review process and notify everyone in the Teams web channel.
type LocalStorageKey =
    | "token"
    | "totp"
    | "reduxVersion"
    | "lastConfirmationFlow"
    | "currentWorkspace"
    | "userSettings"
    | "endpoint"
    | "domainOverride";

const preservedKeys = ["currentWorkspace", "endpoint", "token", "domainOverride"] as const;

type ExpireLocalStorage<T> = {
    value: T;
    expire: number;
};

function isExpireStorageType<T>(value: unknown): value is ExpireLocalStorage<T> {
    return (
        value !== null && typeof value === "object" && "value" in value && "expire" in value && typeof value.expire === "number"
    );
}

function parseValue(value: string) {
    try {
        if (typeof value === "string" && (value.toLowerCase() === "true" || value.toLowerCase() === "false")) {
            return value.toLowerCase() === "true";
        }

        if (!value.startsWith("{")) {
            return value;
        }

        return JSON.parse(value, (_key: string, val: unknown) => {
            if (typeof val === "string" && (val.toLowerCase() === "true" || val.toLowerCase() === "false")) {
                return val.toLowerCase() === "true";
            }

            if (typeof val === "string" && /^\d+\.\d+$/.test(val)) {
                return parseFloat(val);
            }

            return val;
        });
    } catch (_error) {
        return value;
    }
}
function stringifyValue(value: unknown) {
    try {
        return typeof value === "string" ? value : JSON.stringify(value);
    } catch (_error) {
        return null;
    }
}

export const LocalStorage = {
    setItem: (key: LocalStorageKey, payload: unknown, expire?: number) => {
        let value = null;

        if (expire) {
            value = stringifyValue({
                value: payload,
                expire: new Date().getTime() + expire,
            });
        }
        if (!expire) {
            value = stringifyValue(payload);
        }

        if (value) {
            localStorage.setItem(key, value);
        }
    },
    getItem: <T>(key: LocalStorageKey): T | null => {
        const storage = localStorage.getItem(key);
        if (!storage) {
            return null;
        }

        const value = parseValue(storage);

        const isExpireStorage = isExpireStorageType<T>(value);
        if (!isExpireStorage) {
            return value;
        }

        if (TimeService.isDateInThePast(value.expire)) {
            localStorage.removeItem(key);
            return null;
        }

        return value.value;
    },
    removeItem: (key: LocalStorageKey) => {
        localStorage.removeItem(key);
    },
    clear: () => {
        localStorage.clear();
    },
    reset: () => {
        const preservedValues = preservedKeys.reduce<Partial<Record<(typeof preservedKeys)[number], unknown>>>((acc, key) => {
            const value = localStorage.getItem(key);
            if (value !== null) {
                acc[key] = value;
            }
            return acc;
        }, {});

        localStorage.clear();

        Object.entries(preservedValues).forEach(([key, value]) => {
            localStorage.setItem(key, value as string);
        });
    },
};
